import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuthStore } from '@/store/authStore';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  fetchUserSettings,
  updateUserSettings,
  type PersonalSettingsForm,
} from '@/services/settings';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export function PersonalSettings() {
  const { user, updateUser } = useAuthStore();
  const { t } = useTranslation();

  // Use these safe defaults to prevent TypeScript errors
  const initialFormData: PersonalSettingsForm = {
    firstName: '',
    lastName: '',
    email: user?.email || '',
    phone: user?.phone || '',
  };

  const [formData, setFormData] =
    useState<PersonalSettingsForm>(initialFormData);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch user data from the users table
  useEffect(() => {
    const loadUserSettings = async () => {
      if (!user?.id) return;
      setIsLoading(true);

      try {
        const settings = await fetchUserSettings(user.id);

        if (settings) {
          setFormData({
            firstName: settings.first_name,
            lastName: settings.last_name,
            email: settings.email,
            phone: settings.phone || '',
          });
        } else {
          // If failed to fetch settings, use data from auth store
          if (user.name) {
            const nameParts = user.name.split(' ');
            setFormData((prev) => ({
              ...prev,
              firstName: nameParts[0] || '',
              lastName: nameParts.slice(1).join(' ') || '',
            }));
          }
        }
      } catch (error) {
        console.error('Error loading user settings:', error);
        toast.error('Failed to load user settings');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserSettings();
  }, [user?.id, user?.name]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!user?.id) throw new Error('User not authenticated');

      const updatedSettings = await updateUserSettings(user.id, formData);

      if (updatedSettings) {
        // Update local state with combined name
        updateUser({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
        });

        toast.success('Personal information updated successfully');
      }
    } catch (error) {
      console.error('Error updating user settings:', error);
      toast.error('Failed to update personal information');
    } finally {
      setIsLoading(false);
    }
  };

  const fullName = `${formData.firstName} ${formData.lastName}`.trim();
  const avatarName = fullName || user?.name || 'User';
  const avatarInitial = (
    formData.firstName?.[0] ||
    user?.name?.[0] ||
    'U'
  ).toUpperCase();

  return (
    <div className='space-y-6'>
      <div className='flex items-center space-x-4'>
        <Avatar className='h-16 w-16'>
          <AvatarImage
            src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${avatarName}`}
          />
          <AvatarFallback>{avatarInitial}</AvatarFallback>
        </Avatar>
        <div>
          <h2 className='text-xl font-bold'>
            {fullName || user?.name || t('user')}
          </h2>
          <p className='text-sm text-muted-foreground'>
            {formData.email || user?.email}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{t('personalInformation')}</CardTitle>
            <CardDescription>{t('updatePersonalDetails')}</CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <div className='space-y-2'>
                <Label htmlFor='firstName'>{t('firstName')}</Label>
                <Input
                  id='firstName'
                  name='firstName'
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='lastName'>{t('lastName')}</Label>
                <Input
                  id='lastName'
                  name='lastName'
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='email'>{t('emailAddress')}</Label>
              <Input
                id='email'
                name='email'
                type='email'
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='phone'>{t('phoneNumber')}</Label>
              <Input
                id='phone'
                name='phone'
                value={formData.phone || ''}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
          </CardContent>
          <CardFooter>
            <Button type='submit' disabled={isLoading}>
              {isLoading ? t('saving') : t('saveChanges')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}
